import AllPinsFilter from './AllPinsFilter';
import PinTileGrid from '~/modules/design-board/component/all-pins/PinTileGrid';
import NoPinState from '~/modules/design-board/component/all-pins/NoPinState';
import React, { useEffect, useState } from 'react';
import type { PaginationOptions } from '~/modules/root/util/get-pagination-opts.server';
import BottomPagination from '../pagination/BottomPagination';
import MainContentLayout from '~/modules/design-board/component/page-layout/MainContentLayout';
import PinActionBar from '../board-detail/PinActionBar';
import {
    pinsWithCheckedPins,
    updateAllBoards,
    updateCheck,
} from '~/modules/design-board/component/pin-select-bar/PinSelectBar';
import {
    API_ACTIONS,
    DESIGN_BOARD_REMIX_API,
    PROJECT_INDEX_PAGE_SIDE_ITEMS,
} from '../../config';
import type { DeleteItemFromDesignBoardMutation } from 'generated/graphql';
import { useFetcher } from '@remix-run/react';
import { Dialog, useStyles } from '@archipro-design/aria';
import { DeleteDesignBoardDialog } from '../right-side-drawer/DeleteDesignBoardDialog';
import { DeleteDialog } from '../right-side-drawer/RightSideNewBoarderDrawer.style';
import LeftSideNav from '../left-side-nav/LeftSideNav';
import { useUser } from '~/modules/user';
import { useTracker } from '@archipro-website/tracker';
import type { DesignBoardsPinsLoaderData } from '~/routes/_app.member.design-boards.pins';

export type DetailedPinWithChecked = ReturnType<
    typeof pinsWithCheckedPins
>[number];
interface AllPinsProps {
    allPinsData: DesignBoardsPinsLoaderData['allPinsData'];
    pagination: PaginationOptions;
}

const AllPins = ({ allPinsData, pagination }: AllPinsProps) => {
    const [pinListWithChecked, setPinListWithChecked] = React.useState(
        pinsWithCheckedPins(allPinsData)
    );
    const tracker = useTracker();

    useEffect(() => {
        setPinListWithChecked(pinsWithCheckedPins(allPinsData));
    }, [allPinsData, setPinListWithChecked]);

    const { css } = useStyles();
    const user = useUser();
    const isLoggedIn = user.__typename === 'Me';
    const isClient =
        isLoggedIn &&
        user &&
        'Professionals' in user &&
        user.Professionals.length;

    const deleteFetcher = useFetcher<DeleteItemFromDesignBoardMutation>();

    const onDelete = (pinIds = pinsToDelete) => {
        tracker.log('PinDelete', {
            url: new URL(window.location.href),

            data: {
                ElementID: 'designboard_detail_header_deletePin',

                Source: 'designBoard, detail',
            },
        });
        deleteFetcher.submit(
            {
                pinIds,
                _action: API_ACTIONS.DELETE_DESIGN_BOARD_PINS,
            },
            {
                method: 'post',
                action: DESIGN_BOARD_REMIX_API,
            }
        );
    };

    const [pinsToDelete, setPinsToDelete] = useState<string>('');

    const modalClose = () => setPinsToDelete('');

    return (
        <MainContentLayout
            topComponent={<AllPinsFilter />}
            leftSideNav={
                <LeftSideNav
                    navItems={PROJECT_INDEX_PAGE_SIDE_ITEMS}
                    previewTag={isClient ? 'sign up for beta' : 'soon'}
                />
            }
        >
            <PinActionBar
                pins={pinListWithChecked}
                onChange={(checked) =>
                    updateAllBoards({
                        checked,
                        pinListWithChecked,
                        setPinListWithChecked,
                    })
                }
            />

            {allPinsData.length === 0 ? (
                <NoPinState />
            ) : (
                <PinTileGrid
                    onDelete={(pinId) => {
                        setPinsToDelete(`${pinId}`);
                    }}
                    onUpdate={(boardId, checked) =>
                        updateCheck({
                            boardId,
                            checked,
                            pinListWithChecked,
                            setPinListWithChecked,
                        })
                    }
                    pins={pinListWithChecked}
                />
            )}

            <Dialog
                open={pinsToDelete !== ''}
                onCancel={() => modalClose()}
                content={
                    <DeleteDesignBoardDialog
                        modalHeader={
                            pinsToDelete.includes(',')
                                ? `Delete Pins`
                                : `Delete Pin`
                        }
                        modalContent={
                            pinsToDelete.includes(',')
                                ? `Are you sure you would like to delete these pins from this board?`
                                : `Are you sure you would like to delete this pin from this board?`
                        }
                        onDelete={() => {
                            onDelete && onDelete();
                        }}
                        onCancel={() => modalClose()}
                    />
                }
                className={css(DeleteDialog)}
                footer={null}
                header={null}
            />

            <BottomPagination pagination={pagination} itemName="Pin" />
        </MainContentLayout>
    );
};

export default AllPins;
